import { useCallback, useEffect, useState } from 'react'

import { ApiError } from '../../api/client'
import {
  createTelegramSubscriptionLink,
  listTelegramSubscriptions,
  type TelegramChatSubscriptionDto,
} from '../../api/telegramSubscriptions'
import { useAuth } from '../../auth/AuthProvider'

const MESSENGERS_DESCRIPTION =
  'Подключите бота, чтобы получать push-уведомления о статусах заявок.'

type MessengerSettingsPanelProps = {
  /** Встраивается в карточку раздела настроек: без собственной секции и заголовка. */
  embedded?: boolean
}

export const MessengerSettingsPanel = ({ embedded = false }: MessengerSettingsPanelProps) => {
  const { session } = useAuth()
  const [subscriptions, setSubscriptions] = useState<TelegramChatSubscriptionDto[]>([])
  const [loading, setLoading] = useState(true)
  const [creatingLink, setCreatingLink] = useState<'telegram' | 'max' | null>(null)
  const [linkDialog, setLinkDialog] = useState<{ messenger: 'telegram' | 'max'; url: string } | null>(
    null,
  )
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!session) {
      return
    }
    setLoading(true)
    setError(null)
    try {
      const subs = await listTelegramSubscriptions()
      setSubscriptions(subs)
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить подписки')
    } finally {
      setLoading(false)
    }
  }, [session])

  useEffect(() => {
    void load()
  }, [load])

  const handleConnectTelegram = async () => {
    if (!session) {
      return
    }
    setCreatingLink('telegram')
    setError(null)
    setInfo(null)
    try {
      const link = await createTelegramSubscriptionLink({
        botType: 'market',
        purpose: 'order_status',
        organizationId: session.organizationId,
      })
      setLinkDialog({ messenger: 'telegram', url: link.deepLink })
      await load()
    } catch (linkError) {
      if (linkError instanceof ApiError) {
        setError(linkError.message || 'Не удалось создать ссылку для Telegram')
      } else {
        setError('Не удалось создать ссылку для Telegram')
      }
    } finally {
      setCreatingLink(null)
    }
  }

  const handleConnectMax = () => {
    setError(null)
    setInfo('Подключение бота в MAX скоро будет доступно. Пока используйте Telegram.')
  }

  const hasActiveSubscription = subscriptions.some((sub) => sub.active)

  const content = (
    <>
      {embedded ? null : (
        <p className="market-settings-card__desc market-messenger-lead">{MESSENGERS_DESCRIPTION}</p>
      )}

      {loading ? <p className="market-muted">Загрузка…</p> : null}
      {error ? (
        <p className="echat-error" role="alert">
          {error}
        </p>
      ) : null}
      {info ? <p className="market-muted">{info}</p> : null}

      <div className="market-inline-actions market-messenger-actions">
        <button
          type="button"
          className="echat-btn echat-btn--primary echat-btn--sm"
          disabled={creatingLink != null}
          onClick={() => void handleConnectTelegram()}
        >
          {creatingLink === 'telegram'
            ? 'Создаём ссылку…'
            : hasActiveSubscription
              ? 'Telegram подключен'
              : 'Telegram'}
        </button>
        <button
          type="button"
          className="echat-btn echat-btn--ghost echat-btn--sm"
          disabled={creatingLink != null}
          onClick={handleConnectMax}
        >
          MAX
        </button>
      </div>

      {linkDialog ? (
        <div className="market-dialog-backdrop" role="presentation" onClick={() => setLinkDialog(null)}>
          <div
            className="market-dialog"
            role="dialog"
            aria-labelledby="messenger-link-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="messenger-link-title" className="echat-panel__subtitle">
              {linkDialog.messenger === 'telegram'
                ? 'Подключение бота в Telegram'
                : 'Подключение бота в MAX'}
            </h2>
            <p className="market-dialog__link">{linkDialog.url}</p>
            <div className="market-inline-actions">
              <button
                type="button"
                className="echat-btn echat-btn--primary"
                onClick={() => {
                  void navigator.clipboard.writeText(linkDialog.url)
                  setLinkDialog(null)
                }}
              >
                Скопировать
              </button>
              <button
                type="button"
                className="echat-btn echat-btn--ghost"
                onClick={() => setLinkDialog(null)}
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )

  if (embedded) {
    return content
  }

  return (
    <section className="market-settings-section market-settings-section--messengers">
      <h2 className="market-settings-section__title">Мессенджеры</h2>
      {content}
    </section>
  )
}
